/* ═══════════════════════════════════════════
   CDS Cart Bundle — regroupement parent / enfants dans le panier
   - repli / dépli du bloc « produits inclus » (état persistant)
   - suppression du produit parent en cascade sur ses produits inclus
   Thème Sealina (Flow / Eight Themes)
   ═══════════════════════════════════════════ */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'cds-bundle-collapsed:';
  var ANIM_MS = 320;
  /* Une seule cascade à la fois : deux suppressions concurrentes liraient le même
     état de panier et la seconde ressusciterait les lignes de la première. */
  var cascadeEnCours = false;

  /* ── Helpers ── */

  function routeOf(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function cartJsUrl() {
    /* routes.cart_url vaut « /cart » (ou « /fr/cart » en multi-langue) */
    return routeOf('cart_url', '/cart') + '.js';
  }

  function readCollapsed(bundleId) {
    try {
      return window.sessionStorage.getItem(STORAGE_PREFIX + bundleId) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeCollapsed(bundleId, collapsed) {
    try {
      if (collapsed) window.sessionStorage.setItem(STORAGE_PREFIX + bundleId, '1');
      else window.sessionStorage.removeItem(STORAGE_PREFIX + bundleId);
    } catch (e) {
      /* mode privé : on se contente de l'état en mémoire */
    }
  }

  /* ── Repli / dépli ── */

  function setExpanded(bundleEl, expanded, animate) {
    var toggle = bundleEl.querySelector('[data-cds-bundle-toggle]');
    var list = bundleEl.querySelector('[data-cds-bundle-list]');
    if (!toggle || !list) return;

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    bundleEl.classList.toggle('is-collapsed', !expanded);

    if (!animate) {
      list.style.transition = 'none';
      list.style.maxHeight = expanded ? '' : '0px';
      /* visibility, et pas seulement max-height : sinon le contenu replié reste lu
         par les lecteurs d'écran et trouvable au Ctrl+F. */
      list.style.visibility = expanded ? '' : 'hidden';
      list.offsetHeight; /* force reflow avant de rendre la transition */
      list.style.transition = '';
      return;
    }

    if (expanded) {
      list.style.visibility = '';
      list.style.maxHeight = '0px';
      list.offsetHeight;
      list.style.maxHeight = list.scrollHeight + 'px';
      window.setTimeout(function () {
        if (!bundleEl.classList.contains('is-collapsed')) list.style.maxHeight = '';
      }, ANIM_MS);
    } else {
      list.style.maxHeight = list.scrollHeight + 'px';
      list.offsetHeight;
      list.style.maxHeight = '0px';
      /* masqué seulement une fois l'animation terminée, pour ne pas la couper */
      window.setTimeout(function () {
        if (bundleEl.classList.contains('is-collapsed')) list.style.visibility = 'hidden';
      }, ANIM_MS);
    }
  }

  /* Le tiroir se re-rend par innerHTML : le Liquid revient toujours « déplié ».
     On réapplique l'état mémorisé après chaque rendu. */
  function restoreAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cds-bundle]').forEach(function (bundleEl) {
      var id = bundleEl.getAttribute('data-cds-bundle-id') || '';
      if (bundleEl.dataset.cdsBundleRestored === id) return;
      bundleEl.dataset.cdsBundleRestored = id;
      setExpanded(bundleEl, !readCollapsed(id), false);
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-cds-bundle-toggle]');
    if (!toggle) return;
    var bundleEl = toggle.closest('[data-cds-bundle]');
    if (!bundleEl) return;
    e.preventDefault();

    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    setExpanded(bundleEl, !expanded, true);
    writeCollapsed(bundleEl.getAttribute('data-cds-bundle-id') || '', expanded);
  });

  /* ── Suppression en cascade ── */

  function fetchCart() {
    return fetch(cartJsUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json();
    });
  }

  function isInDrawer(el) {
    return !!(el.closest && el.closest('cart-drawer'));
  }

  /* Le tiroir est rendu sur toutes les pages, y compris /cart. Or la page panier
     porte ses propres inputs name="updates[]" positionnels, soumis par « Mettre à
     jour » et « Passer à la caisse » : les laisser périmés fausserait la commande.
     On recharge donc dès que la page panier est affichée, quel que soit le
     déclencheur utilisé. */
  function isCartPage() {
    return !!document.querySelector('[data-wetheme-section-type="template--cart"]');
  }

  function refreshCartCount() {
    if (!window.wetheme || typeof window.wetheme.updateCartCount !== 'function') return;
    fetchCart()
      .then(function (cart) {
        window.wetheme.updateCartCount(cart);
      })
      .catch(function () {});
  }

  function removeBundle(trigger) {
    var bundleId = trigger.getAttribute('data-cds-bundle-id');
    if (!bundleId) {
      cascadeEnCours = false;
      return Promise.resolve();
    }

    var parentQty = parseInt(trigger.getAttribute('data-cds-parent-quantity'), 10) || 0;
    var hasGiftWrap = trigger.getAttribute('data-cds-has-giftwrap') === 'true';
    var giftWrapVariantId = parseInt(trigger.getAttribute('data-cds-giftwrap-variant-id'), 10) || 0;
    var inDrawer = isInDrawer(trigger);

    var card =
      (trigger.closest && (trigger.closest('.cart-drawer__product_card') || trigger.closest('tr'))) || null;
    if (card) card.classList.add('cds-cart-bundle--removing');
    trigger.setAttribute('aria-busy', 'true');

    return fetchCart()
      .then(function (cart) {
        /* Tableau positionnel : une entrée par ligne, dans l'ordre du panier.
           Forme la plus sûre — pas d'ambiguïté de clé, et atomique. */
        var updates = cart.items.map(function (it) {
          var props = it.properties || {};
          if (props['_bundle_id'] === bundleId) return 0;

          /* L'emballage cadeau n'est pas rattaché au bundle (il fusionne entre
             produits) : on le décrémente de la quantité du parent, comme le fait
             le thème lors d'un changement de quantité. */
          if (
            hasGiftWrap &&
            giftWrapVariantId &&
            it.variant_id === giftWrapVariantId &&
            !props['_bundle_id']
          ) {
            return Math.max(0, it.quantity - parentQty);
          }

          return it.quantity;
        });

        var body = { updates: updates };
        if (inDrawer) body.sections = 'cart-drawer';

        return fetch(routeOf('cart_update_url', '/cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body)
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update a répondu ' + response.status);
        return response.json();
      })
      .then(function (data) {
        if (!inDrawer || isCartPage()) {
          window.location.reload();
          return;
        }
        if (window.eventBus) {
          if (data && data.sections && data.sections['cart-drawer']) {
            window.eventBus.emit('update:cart:drawer', data);
          } else {
            window.eventBus.emit('render:cart:drawer');
          }
        }
        refreshCartCount();
      })
      .catch(function (error) {
        console.error('CDS Cart Bundle — suppression impossible :', error);
        if (card) card.classList.remove('cds-cart-bundle--removing');
        trigger.removeAttribute('aria-busy');
        /* Surtout ne pas suivre le lien natif en repli : il ne supprime que la ligne
           parente et laisserait les produits inclus seuls au panier. On rend la main
           au client, qui peut réessayer. */
        window.alert(
          "La suppression n'a pas abouti. Vérifiez votre connexion et réessayez."
        );
      })
      .then(function () {
        cascadeEnCours = false;
        /* En temps normal le re-rendu du tiroir remplace toute la carte. Si ce rendu
           n'aboutit pas, sans ce nettoyage la carte resterait grisée et inerte. */
        if (card) card.classList.remove('cds-cart-bundle--removing');
        trigger.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-cds-bundle-remove]');
    if (!trigger) return;
    e.preventDefault();
    if (cascadeEnCours) return;
    if (trigger.getAttribute('aria-busy') === 'true') return;
    cascadeEnCours = true;
    removeBundle(trigger);
  });

  /* ── Cycle de vie ── */

  function init() {
    restoreAll(document);

    /* Le tiroir remplace l'innerHTML de ses conteneurs : on réapplique l'état
       du repli à chaque rendu. Un MutationObserver est le seul point d'accroche
       fiable, les écouteurs internes ne survivant pas au remplacement. */
    var drawer = document.querySelector('cart-drawer');
    if (drawer && window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          restoreAll(drawer);
        });
      }).observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
